import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { 
  AdvancedFilters, 
  FilterState, 
  SavedView, 
  FilterOptionsState,
  ActiveFilter,
  DateRange 
} from '@/types/filters';
import { UserRole, TicketStatus, TicketPriority, TicketSource } from '@/types/api';
import { UserService } from '@/services/userService';

/**
 * Advanced Filters Hook
 * 
 * Comprehensive state management for the filtering system:
 * - Filter state management with URL synchronization
 * - Saved views functionality
 * - Filter options loading
 * - Active filters computation
 * - Local storage persistence
 */

const STORAGE_KEY = 'supportdesk_filter_state';
const URL_PARAM_PREFIX = 'f_';
const SUPPORTED_FILTER_KEYS = new Set([
  'search', 'assignedAgents', 'unassignedOnly', 'myTicketsOnly', 'statuses',
  'priorities', 'sources', 'companies', 'createdDateRange', 'updatedDateRange',
  'hasAttachments', 'escalated'
]);

// Default filter values
const DEFAULT_FILTERS: AdvancedFilters = {
  search: '',
  assignedAgents: [],
  assignedGroups: [],
  unassignedOnly: false,
  myTicketsOnly: false,
  statuses: [],
  priorities: [],
  sources: [],
  types: [],
  customers: [],
  companies: [],
  vipCustomersOnly: false,
  customerSatisfactionRange: null,
  createdDateRange: null,
  updatedDateRange: null,
  dueDateRange: null,
  closedDateRange: null,
  customFields: [],
  tags: [],
  excludeTags: [],
  hasAttachments: null,
  hasConversations: null,
  isOverdue: null,
  escalated: null
};

const normalizeSupportedFilters = (input: Partial<AdvancedFilters>): AdvancedFilters => {
  const supported = Object.fromEntries(
    Object.entries(input).filter(([key]) => SUPPORTED_FILTER_KEYS.has(key))
  ) as Partial<AdvancedFilters>;
  return {
    ...DEFAULT_FILTERS,
    ...supported,
    statuses: (supported.statuses ?? []).filter(status => status !== TicketStatus.InProgress)
  };
};

// Default filter options state
const DEFAULT_FILTER_OPTIONS: FilterOptionsState = {
  agents: [],
  groups: [],
  companies: [],
  customers: [],
  tags: [],
  customFields: [],
  loading: {
    agents: false,
    groups: false,
    companies: false,
    customers: false,
    tags: false,
    customFields: false
  }
};

interface UseAdvancedFiltersOptions {
  userId: string;
  userRole: UserRole;
  enableUrlSync?: boolean;
  enableLocalStorage?: boolean;
  onFiltersChange?: (filters: AdvancedFilters) => void;
  onFilterAnalytics?: (event: string, data: any) => void;
}

// Initialize userService instance
const userService = new UserService();

export function useAdvancedFilters({
  userId,
  userRole,
  enableUrlSync = true,
  enableLocalStorage = true,
  onFiltersChange,
  onFilterAnalytics
}: UseAdvancedFiltersOptions) {
  
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Main filter state
  const [filters, setFilters] = useState<AdvancedFilters>(DEFAULT_FILTERS);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [groupExpanded, setGroupExpanded] = useState<Record<string, boolean>>({});
  
  const storageKey = `saved_views_${userId || 'default'}`;

  // Saved views state with localStorage persistence
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => {
    if (typeof window !== 'undefined' && enableLocalStorage) {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (e) {
        console.error('Error loading saved views from localStorage:', e);
      }
    }
    return [
      {
        id: 'view-open-tickets',
        name: 'Open Tickets',
        description: 'Tickets currently open',
        filters: { ...DEFAULT_FILTERS, statuses: [TicketStatus.Open] },
        isPublic: false,
        isDefault: true,
        createdBy: 'system',
        createdByName: 'Built in',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        usageCount: 0
      },
      {
        id: 'view-urgent-tickets',
        name: 'Urgent & High Priority',
        description: 'Tickets requiring immediate attention',
        filters: { ...DEFAULT_FILTERS, priorities: [TicketPriority.Urgent, TicketPriority.High] },
        isPublic: false,
        isDefault: false,
        createdBy: 'system',
        createdByName: 'Built in',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        usageCount: 0
      }
    ];
  });

  // Persist saved views to localStorage whenever they change
  useEffect(() => {
    if (enableLocalStorage && typeof window !== 'undefined') {
      try {
        localStorage.setItem(storageKey, JSON.stringify(savedViews));
      } catch (e) {
        console.error('Error saving views to localStorage:', e);
      }
    }
  }, [savedViews, storageKey, enableLocalStorage]);

  const [currentViewId, setCurrentViewId] = useState<string | null>(null);
  const [savedViewsLoading, setSavedViewsLoading] = useState(false);
  const [savedViewsError, setSavedViewsError] = useState<string | null>(null);
  
  // Filter options state
  const [filterOptions, setFilterOptions] = useState<FilterOptionsState>(DEFAULT_FILTER_OPTIONS);
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compute active filters for display
  const activeFilters = useMemo((): ActiveFilter[] => {
    const active: ActiveFilter[] = [];

    // Search filter
    if (filters.search) {
      active.push({
        id: 'search',
        groupId: 'search',
        filterId: 'search',
        label: 'Search',
        value: filters.search,
        displayValue: `"${filters.search}"`,
        removable: true
      });
    }

    // Status filters
    filters.statuses.forEach(status => {
      active.push({
        id: `status-${status}`,
        groupId: 'properties',
        filterId: 'statuses',
        label: 'Status',
        value: status,
        displayValue: status,
        removable: true,
        color: getStatusColor(status)
      });
    });

    // Priority filters
    filters.priorities.forEach(priority => {
      active.push({
        id: `priority-${priority}`,
        groupId: 'properties',
        filterId: 'priorities',
        label: 'Priority',
        value: priority,
        displayValue: priority,
        removable: true,
        color: getPriorityColor(priority)
      });
    });

    // Assignment filters
    if (filters.unassignedOnly) {
      active.push({
        id: 'unassigned',
        groupId: 'assignment',
        filterId: 'unassignedOnly',
        label: 'Assignment',
        value: true,
        displayValue: 'Unassigned',
        removable: true
      });
    }

    if (filters.myTicketsOnly) {
      active.push({
        id: 'mytickets',
        groupId: 'assignment',
        filterId: 'myTicketsOnly',
        label: 'Assignment',
        value: true,
        displayValue: 'My Tickets',
        removable: true
      });
    }

    // Assigned agents
    filters.assignedAgents.forEach(agentId => {
      const agent = filterOptions.agents.find(a => a.userId === agentId);
      active.push({
        id: `agent-${agentId}`,
        groupId: 'assignment',
        filterId: 'assignedAgents',
        label: 'Agent',
        value: agentId,
        displayValue: agent?.userName || agentId,
        removable: true
      });
    });

    // Companies
    filters.companies.forEach(companyId => {
      const company = filterOptions.companies.find(c => c.companyId === companyId);
      active.push({
        id: `company-${companyId}`,
        groupId: 'customer',
        filterId: 'companies',
        label: 'Company',
        value: companyId,
        displayValue: company?.companyName || companyId,
        removable: true
      });
    });

    filters.sources.forEach(source => active.push({
      id: `source-${source}`, groupId: 'properties', filterId: 'sources',
      label: 'Source', value: source, displayValue: source, removable: true
    }));

    // Date ranges
    if (filters.createdDateRange) {
      active.push({
        id: 'created-date',
        groupId: 'dates',
        filterId: 'createdDateRange',
        label: 'Created',
        value: filters.createdDateRange,
        displayValue: formatDateRange(filters.createdDateRange),
        removable: true
      });
    }

    if (filters.updatedDateRange) {
      active.push({
        id: 'updated-date',
        groupId: 'dates',
        filterId: 'updatedDateRange',
        label: 'Updated',
        value: filters.updatedDateRange,
        displayValue: formatDateRange(filters.updatedDateRange),
        removable: true
      });
    }

    // Tags
    filters.tags.forEach(tag => {
      active.push({
        id: `tag-${tag}`,
        groupId: 'advanced',
        filterId: 'tags',
        label: 'Tag',
        value: tag,
        displayValue: tag,
        removable: true
      });
    });

    // Advanced options
    if (filters.hasAttachments === true) {
      active.push({
        id: 'has-attachments',
        groupId: 'advanced',
        filterId: 'hasAttachments',
        label: 'Attachments',
        value: true,
        displayValue: 'Has Attachments',
        removable: true
      });
    }

    if (filters.escalated === true) active.push({
      id: 'escalated', groupId: 'advanced', filterId: 'escalated',
      label: 'Escalated', value: true, displayValue: 'Escalated', removable: true
    });

    return active;
  }, [filters, filterOptions]);

  // Load initial state from localStorage and URL
  useEffect(() => {
    let initialFilters = { ...DEFAULT_FILTERS };
    
    // Load from localStorage
    if (enableLocalStorage) {
      try {
        const stored = localStorage.getItem(`${STORAGE_KEY}_${userId}`);
        if (stored) {
          const parsedFilters = JSON.parse(stored);
          initialFilters = normalizeSupportedFilters(parsedFilters);
        }
      } catch (error) {
        console.warn('Failed to load filters from localStorage:', error);
      }
    }
    
    // Load from URL (overrides localStorage)
    if (enableUrlSync) {
      const urlFilters = parseFiltersFromUrl(searchParams);
      initialFilters = normalizeSupportedFilters({ ...initialFilters, ...urlFilters });
    }
    
    setFilters(initialFilters);
  }, [userId, enableLocalStorage, enableUrlSync, searchParams]);

  // Save to localStorage when filters change
  useEffect(() => {
    if (enableLocalStorage) {
      try {
        localStorage.setItem(`${STORAGE_KEY}_${userId}`, JSON.stringify(filters));
      } catch (error) {
        console.warn('Failed to save filters to localStorage:', error);
      }
    }
  }, [filters, userId, enableLocalStorage]);

  // Update URL when filters change
  useEffect(() => {
    if (enableUrlSync) {
      updateUrlFromFilters(filters, setSearchParams, searchParams);
    }
  }, [filters, enableUrlSync, setSearchParams]);

  // Notify parent component when filters change
  useEffect(() => {
    onFiltersChange?.(filters);
  }, [filters, onFiltersChange]);

  // Load filter options
  const loadFilterOptions = useCallback(async () => {
    setFilterOptions(prev => ({
      ...prev,
      loading: {
        agents: true,
        groups: true,
        companies: true,
        customers: true,
        tags: true,
        customFields: true
      }
    }));

    try {
      // Load real data from backend APIs
      const agentsResponse = await userService.getAllAgents();
      // The company directory is restricted to managers and administrators.
      // A denied company request must not erase an agent's valid assignee list.
      const companiesResponse = userRole >= UserRole.Manager
        ? await userService.getCompanies()
        : null;
      
      // Transform agents data with robust response parsing
      const rawAgents = agentsResponse.success ? agentsResponse.data?.data ?? [] : [];

      const agents = rawAgents.map(user => ({
        userId: user.id,
        userName: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        userEmail: user.email,
        userRole: user.role,
        avatar: user.profileImageUrl
      }));

      // Transform companies data with proper null checking
      const rawCompanies = companiesResponse?.success ? companiesResponse.data ?? [] : [];

      const companies = rawCompanies.map(company => ({
        companyId: company.id,
        companyName: company.name,
        domain: company.domain || '',
        isVip: false
      }));
      
      setFilterOptions(prev => ({
        ...prev,
        agents: agents,
        groups: [],
        companies: companies,
        customers: [],
        tags: [],
        customFields: [],
        loading: {
          agents: false,
          groups: false,
          companies: false,
          customers: false,
          tags: false,
          customFields: false
        }
      }));
      
    } catch (error) {
      console.error('Failed to load filter options:', error);

      // Ensure we have safe defaults even on error
      setFilterOptions(prev => ({
        ...prev,
        agents: [],
        groups: [
          { id: 'support', name: 'Support Team', agentCount: 0 },
          { id: 'technical', name: 'Technical Team', agentCount: 0 },
          { id: 'sales', name: 'Sales Team', agentCount: 0 }
        ],
        companies: [],
        customers: [],
        tags: [],
        customFields: [],
        loading: {
          agents: false,
          groups: false,
          companies: false,
          customers: false,
          tags: false,
          customFields: false
        }
      }));
    }
  }, [userRole]);

  // Auto-load filter options on mount
  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Update filters
  const updateFilters = useCallback((newFilters: AdvancedFilters) => {
    setFilters(normalizeSupportedFilters(newFilters));
    setCurrentViewId(null); // Clear current view when filters change
    
    // Analytics
    onFilterAnalytics?.('filters_updated', {
      userId,
      filters: newFilters,
      activeFilterCount: Object.values(newFilters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'boolean') return value === true;
        if (value && typeof value === 'object') return Object.keys(value).length > 0;
        return value && value !== '';
      }).length,
      timestamp: Date.now()
    });
  }, [userId, onFilterAnalytics]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setCurrentViewId(null);
    
    onFilterAnalytics?.('filters_cleared', {
      userId,
      timestamp: Date.now()
    });
  }, [userId, onFilterAnalytics]);

  // Remove specific filter
  const removeFilter = useCallback((filterId: string) => {
    const filter = activeFilters.find(f => f.id === filterId);
    if (!filter) return;

    const newFilters = { ...filters };
    
    switch (filter.filterId) {
      case 'search':
        newFilters.search = '';
        break;
      case 'statuses':
        newFilters.statuses = newFilters.statuses.filter(s => s !== filter.value);
        break;
      case 'priorities':
        newFilters.priorities = newFilters.priorities.filter(p => p !== filter.value);
        break;
      case 'sources':
        newFilters.sources = newFilters.sources.filter(source => source !== filter.value);
        break;
      case 'assignedAgents':
        newFilters.assignedAgents = newFilters.assignedAgents.filter(a => a !== filter.value);
        break;
      case 'companies':
        newFilters.companies = newFilters.companies.filter(c => c !== filter.value);
        break;
      case 'tags':
        newFilters.tags = newFilters.tags.filter(t => t !== filter.value);
        break;
      case 'unassignedOnly':
        newFilters.unassignedOnly = false;
        break;
      case 'myTicketsOnly':
        newFilters.myTicketsOnly = false;
        break;
      case 'vipCustomersOnly':
        newFilters.vipCustomersOnly = false;
        break;
      case 'createdDateRange':
        newFilters.createdDateRange = null;
        break;
      case 'updatedDateRange':
        newFilters.updatedDateRange = null;
        break;
      case 'hasAttachments':
        newFilters.hasAttachments = null;
        break;
      case 'escalated':
        newFilters.escalated = null;
        break;
      case 'isOverdue':
        newFilters.isOverdue = null;
        break;
    }

    updateFilters(newFilters);
  }, [activeFilters, filters, updateFilters]);

  // Saved views functions
  const saveView = useCallback(async (name: string, description?: string, isPublic?: boolean) => {
    setSavedViewsLoading(true);
    setSavedViewsError(null);
    
    try {
      // In a real app, this would be an API call
      const newView: SavedView = {
        id: `view-${Date.now()}`,
        name,
        description,
        filters: { ...filters },
        isPublic: false,
        isDefault: false,
        createdBy: userId,
        createdByName: 'Current User', // In real app, get from user context
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        usageCount: 0
      };
      
      setSavedViews(prev => [...prev, newView]);
      setCurrentViewId(newView.id);
      
      onFilterAnalytics?.('view_saved', {
        userId,
        viewId: newView.id,
        viewName: name,
        isPublic: isPublic || false,
        timestamp: Date.now()
      });
      
    } catch (error) {
      setSavedViewsError('Failed to save view');
      console.error('Failed to save view:', error);
    } finally {
      setSavedViewsLoading(false);
    }
  }, [filters, userId, onFilterAnalytics]);

  const loadView = useCallback(async (viewId: string) => {
    const view = savedViews.find(v => v.id === viewId);
    if (!view) return;
    
    setFilters(normalizeSupportedFilters(view.filters));
    setCurrentViewId(viewId);
    
    // Update usage count
    setSavedViews(prev => 
      prev.map(v => 
        v.id === viewId 
          ? { ...v, usageCount: (v.usageCount || 0) + 1 }
          : v
      )
    );
    
    onFilterAnalytics?.('view_loaded', {
      userId,
      viewId,
      viewName: view.name,
      timestamp: Date.now()
    });
  }, [savedViews, userId, onFilterAnalytics]);

  const deleteView = useCallback(async (viewId: string) => {
    setSavedViews(prev => prev.filter(v => v.id !== viewId));
    
    if (currentViewId === viewId) {
      setCurrentViewId(null);
    }
    
    onFilterAnalytics?.('view_deleted', {
      userId,
      viewId,
      timestamp: Date.now()
    });
  }, [currentViewId, userId, onFilterAnalytics]);

  const setDefaultView = useCallback(async (viewId: string) => {
    setSavedViews(prev => 
      prev.map(view => ({
        ...view,
        isDefault: view.id === viewId
      }))
    );
  }, []);

  // Toggle sidebar
  const toggleSidebar = useCallback(() => {
    setSidebarExpanded(prev => !prev);
  }, []);

  // Toggle group expansion
  const toggleGroup = useCallback((groupId: string) => {
    setGroupExpanded(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  }, []);

  return {
    // Filter state
    filters,
    updateFilters,
    clearAllFilters,
    removeFilter,
    activeFilters,
    
    // UI state
    sidebarExpanded,
    toggleSidebar,
    groupExpanded,
    toggleGroup,
    
    // Saved views
    savedViews,
    currentViewId,
    saveView,
    loadView,
    deleteView,
    setDefaultView,
    savedViewsLoading,
    savedViewsError,
    
    // Filter options
    filterOptions,
    loadFilterOptions,
    
    // Loading and error states
    loading,
    error
  };
}

// Helper functions
function getStatusColor(status: TicketStatus): string {
  const colors: Record<TicketStatus, string> = {
    [TicketStatus.New]: '#6B7280',
    [TicketStatus.Open]: '#10B981',
    [TicketStatus.InProgress]: '#3B82F6',
    [TicketStatus.Pending]: '#3B82F6',
    [TicketStatus.OnHold]: '#F59E0B',
    [TicketStatus.Resolved]: '#8B5CF6',
    [TicketStatus.Closed]: '#6B7280',
    [TicketStatus.Cancelled]: '#EF4444'
  };
  return colors[status] || '#6B7280';
}

function getPriorityColor(priority: TicketPriority): string {
  const colors: Record<TicketPriority, string> = {
    [TicketPriority.Low]: '#10B981',
    [TicketPriority.Medium]: '#F59E0B',
    [TicketPriority.High]: '#EF4444',
    [TicketPriority.Urgent]: '#DC2626',
    [TicketPriority.Critical]: '#7C2D12'
  };
  return colors[priority] || '#6B7280';
}

function formatDateRange(range: DateRange): string {
  if (range.preset) {
    const presetLabels: Record<string, string> = {
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This Week',
      lastWeek: 'Last Week',
      thisMonth: 'This Month',
      lastMonth: 'Last Month',
      last7Days: 'Last 7 Days',
      last30Days: 'Last 30 Days',
      last90Days: 'Last 90 Days'
    };
    return presetLabels[range.preset] || 'Custom Range';
  }
  
  if (range.from && range.to) {
    return `${formatDate(range.from)} - ${formatDate(range.to)}`;
  }
  
  if (range.from) {
    return `From ${formatDate(range.from)}`;
  }
  
  if (range.to) {
    return `Until ${formatDate(range.to)}`;
  }
  
  return 'Custom Range';
}

function formatDate(date: string | Date): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function parseFiltersFromUrl(searchParams: URLSearchParams): Partial<AdvancedFilters> {
  const filters: Partial<AdvancedFilters> = {};
  
  // Parse each filter type from URL parameters
  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith(URL_PARAM_PREFIX)) {
      const filterKey = key.substring(URL_PARAM_PREFIX.length);
      
      try {
        switch (filterKey) {
          case 'search':
            filters.search = value;
            break;
          case 'statuses':
            filters.statuses = value.split(',').filter((item): item is TicketStatus =>
              item !== TicketStatus.InProgress && Object.values(TicketStatus).includes(item as TicketStatus));
            break;
          case 'priorities':
            filters.priorities = value.split(',').filter((item): item is TicketPriority =>
              Object.values(TicketPriority).includes(item as TicketPriority));
            break;
          case 'sources':
            filters.sources = value.split(',').filter((item): item is TicketSource =>
              Object.values(TicketSource).includes(item as TicketSource));
            break;
          case 'assignedAgents':
          case 'companies':
            filters[filterKey] = value.split(',').filter(Boolean);
            break;
          case 'unassignedOnly':
          case 'myTicketsOnly':
            filters[filterKey] = value === 'true';
            break;
          case 'hasAttachments':
          case 'escalated':
            filters[filterKey] = value === 'true';
            break;
          case 'createdDateRange':
          case 'updatedDateRange':
            if (value) {
              filters[filterKey] = JSON.parse(value) as DateRange;
            }
            break;
        }
      } catch (error) {
        console.warn(`Failed to parse URL filter ${filterKey}:`, error);
      }
    }
  }
  
  return filters;
}

function updateUrlFromFilters(filters: AdvancedFilters, setSearchParams: (params: URLSearchParams) => void, currentParams: URLSearchParams) {
  // Keep navigation context (requester, view) when synchronising the filter fields.
  const newSearchParams = new URLSearchParams(currentParams);
  for (const key of [...newSearchParams.keys()]) {
    if (key.startsWith(URL_PARAM_PREFIX)) newSearchParams.delete(key);
  }
  
  // Add non-empty filter values to URL
  Object.entries(filters).forEach(([key, value]) => {
    if (!SUPPORTED_FILTER_KEYS.has(key)) return;
    if (value !== null && value !== undefined && value !== '' && 
        !(Array.isArray(value) && value.length === 0)) {
      
      const paramKey = `${URL_PARAM_PREFIX}${key}`;
      
      if (Array.isArray(value)) {
        newSearchParams.set(paramKey, value.join(','));
      } else if (typeof value === 'object') {
        newSearchParams.set(paramKey, JSON.stringify(value));
      } else if (typeof value === 'boolean') {
        newSearchParams.set(paramKey, value.toString());
      } else {
        newSearchParams.set(paramKey, value.toString());
      }
    }
  });
  
  if (newSearchParams.toString() !== currentParams.toString()) {
    setSearchParams(newSearchParams);
  }
}
